import { createApi } from '@reduxjs/toolkit/query/react';

import { setPastAppointments, setUpcomingAppointments } from 'store/appointments/appointmentsSlice';

import { logDataDogEvent } from 'utils/datadog';
import { AppointmentListTypes } from 'utils/enums';
import { parseUpcomingAppointment, withClockOffset } from 'utils/appointment';
import { baseQueryWithReauth } from 'utils/services';

import {
  AddAppointmentReqProps,
  AddAppointmentResProps,
  AddLegacyVideoAppointmentReqProps,
  AppointmentEligibilityReqProps,
  CallLogTracksReqProps,
  CallLogTracksResProps,
  CancelAppointmentReqProps,
  CancelAppointmentResProps,
  CollectUpfrontCopayReqProps,
  CollectUpfrontCopayResProps,
  CheckPricesPredictReqProps,
  CheckPricesPredictResProps,
  GetCancellationFeePreviewReqProps,
  GetCancellationFeePreviewResProps,
  GetAppointmentMethodsReqProps,
  GetAppointmentMethodsResProps,
  GetAppointmentReqProps,
  GetAppointmentResProps,
  GetAppointmentsReqProps,
  GetAppointmentsResProps,
  GetAppointmentTypeReqProps,
  GetAppointmentTypeResProps,
  GetIntakeRequirementsReqProps,
  GetIntakeRequirementsResProps,
  GetNoShowPolicyReqProps,
  GetNoShowPolicyResProps,
  GetQueueEstimatePreviewReqProps,
  GetQueueEstimatePreviewResProps,
  GetUpfrontCopayPreviewReqProps,
  GetUpfrontCopayPreviewResProps,
  GetTokenZoomReqProps,
  GetTokenZoomResProps,
  GuestUserAppointmentProps,
  MissedBannerViewedReqProps,
  MissedBannerViewedResProps,
  PoorConnectionReqProps,
  PoorConnectionResProps,
  RescheduleAppointmentReqProps,
  RescheduleAppointmentResProps,
  SkipIdVerificationReqProps,
  SkipIdVerificationResProps,
  UpdateAppointmentReqProps,
  UpdateAppointmentResProps,
  UpdateCallErrorsReqProps,
  UpdateCallErrorsResProps,
  UploadFilesReqProps,
  UploadFilesResProps
} from './appointments.types';

export const appointmentsApi = createApi({
  baseQuery: baseQueryWithReauth,
  endpoints: (build) => ({
    addAppointment: build.mutation<
      AddAppointmentResProps,
      AddAppointmentReqProps | AddLegacyVideoAppointmentReqProps
    >({
      query: (body) => ({
        body,
        method: 'POST',
        url: '/appointments/schedule'
      })
    }),
    callLogTracks: build.mutation<CallLogTracksResProps, CallLogTracksReqProps>({
      query: ({ appointmentId, ...body }) => ({
        body,
        headers: { Platform: 'web-patient' },
        method: 'POST',
        url: `/appointments/${appointmentId}/call-log-tracks`
      })
    }),
    cancelAppointment: build.mutation<CancelAppointmentResProps, CancelAppointmentReqProps>({
      query: ({ appointmentId, ...body }) => ({
        body,
        method: 'PATCH',
        url: `/appointments/${appointmentId}/cancel`
      })
    }),
    cancelAppointmentForGuest: build.mutation<CancelAppointmentResProps, GuestUserAppointmentProps>(
      {
        query: ({ accessToken, id, ...body }) => ({
          body,
          headers: {
            'API-KEY': import.meta.env.VITE_API_KEY,
            Authorization: accessToken
          },
          method: 'PATCH',
          url: `/appointments/${id}/cancel`
        })
      }
    ),
    checkApptEligibility: build.mutation<unknown, AppointmentEligibilityReqProps>({
      query: (body) => ({
        body,
        method: 'POST',
        url: `/appointments/eligibility`
      })
    }),
    checkPricesPredict: build.mutation<CheckPricesPredictResProps, CheckPricesPredictReqProps>({
      query: (body) => ({
        body,
        method: 'POST',
        url: '/appointments/payment-predict'
      })
    }),
    collectUpfrontCopay: build.mutation<CollectUpfrontCopayResProps, CollectUpfrontCopayReqProps>({
      invalidatesTags: (_result, error, { appointmentId }) =>
        error ? [] : [{ id: appointmentId, type: 'UpfrontCopay' }],
      query: ({ appointmentId }) => ({
        method: 'POST',
        url: `/appointments/${appointmentId}/upfront-copay/collect`
      })
    }),
    getAppointment: build.query<GetAppointmentResProps, GetAppointmentReqProps>({
      keepUnusedDataFor: 1,
      query: (id) => `/appointments/${id}`,
      transformResponse: (response: GetAppointmentResProps) =>
        response?.data ? { ...response, data: withClockOffset(response.data) } : response
    }),
    getAppointmentInfoForGuest: build.query<GetAppointmentResProps, GuestUserAppointmentProps>({
      keepUnusedDataFor: 1,
      query: ({ accessToken, id }) => ({
        headers: {
          'API-KEY': import.meta.env.VITE_API_KEY,
          Authorization: accessToken
        },
        url: `/appointments/${id}`
      })
    }),
    getAppointmentMethods: build.query<
      GetAppointmentMethodsResProps,
      GetAppointmentMethodsReqProps
    >({
      query: ({ appointmentTypeId, ...params }) => ({
        params,
        url: `/appointment-types/${appointmentTypeId}/availability`
      })
    }),
    getAppointments: build.query<GetAppointmentsResProps, GetAppointmentsReqProps>({
      keepUnusedDataFor: 1,
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data) {
            switch (arg.appointmentListType) {
              case AppointmentListTypes.Upcoming:
                dispatch(setUpcomingAppointments([...data.data.map(parseUpcomingAppointment)]));
                break;
              case AppointmentListTypes.Past:
                dispatch(setPastAppointments(data.data));
                break;
            }
          }
        } catch (e) {
          logDataDogEvent({
            context: { error: e },
            level: 'error',
            message: 'getAppointments failed'
          });
        }
      },
      query: (params) => ({
        params: { ...params, limit: 50 },
        url: '/appointments'
      })
    }),
    getAppointmentType: build.query<GetAppointmentTypeResProps, GetAppointmentTypeReqProps>({
      keepUnusedDataFor: 1,
      query: ({ id }) => ({
        url: `/appointment-types/${id}`
      })
    }),
    getCancellationFeePreview: build.query<
      GetCancellationFeePreviewResProps,
      GetCancellationFeePreviewReqProps
    >({
      keepUnusedDataFor: 0,
      query: (appointmentId) => `/appointments/${appointmentId}/cancellation-fee-preview`
    }),
    getIntakeRequirements: build.query<
      GetIntakeRequirementsResProps,
      GetIntakeRequirementsReqProps
    >({
      query: ({ appointmentTypeId, modalityType }) => ({
        url: `/appointment-types/${appointmentTypeId}/modalities/${modalityType}/intake-requirements`
      })
    }),
    getNoShowPolicy: build.query<GetNoShowPolicyResProps, GetNoShowPolicyReqProps>({
      keepUnusedDataFor: 0,
      query: ({ appointmentTypeId, ...params }) => ({
        params,
        url: `/appointment-types/${appointmentTypeId}/no-show-policy`
      })
    }),
    getQueueEstimatePreview: build.query<
      GetQueueEstimatePreviewResProps,
      GetQueueEstimatePreviewReqProps
    >({
      keepUnusedDataFor: 0,
      query: ({ appointmentTypeId, preferredProviderGender }) => ({
        params: preferredProviderGender ? { preferredProviderGender } : undefined,
        url: `/appointment-types/${appointmentTypeId}/queue-estimate-preview`
      })
    }),
    getTokenZoom: build.query<GetTokenZoomResProps, GetTokenZoomReqProps>({
      query: (id) => `appointments/${id}/call-token-zoom?type=video`
    }),
    getUpfrontCopayPreview: build.query<
      GetUpfrontCopayPreviewResProps,
      GetUpfrontCopayPreviewReqProps
    >({
      keepUnusedDataFor: 0,
      providesTags: (_result, _error, appointmentId) => [
        { id: appointmentId, type: 'UpfrontCopay' }
      ],
      query: (appointmentId) => `/appointments/${appointmentId}/upfront-copay-preview`
    }),
    missedBannerViewed: build.mutation<MissedBannerViewedResProps, MissedBannerViewedReqProps>({
      query: ({ appointmentId }) => ({
        method: 'PATCH',
        url: `/appointments/${appointmentId}/missed-banner-viewed`
      })
    }),
    poorConnection: build.mutation<PoorConnectionResProps, PoorConnectionReqProps>({
      query: ({ appointmentId, ...body }) => ({
        body,
        method: 'POST',
        url: `/appointments/${appointmentId}/poor-connection`
      })
    }),
    rescheduleAppointment: build.mutation<
      RescheduleAppointmentResProps,
      RescheduleAppointmentReqProps
    >({
      query: ({ appointmentId, ...body }) => ({
        body,
        method: 'PATCH',
        url: `/appointments/${appointmentId}/reschedule`
      })
    }),
    skipIdVerification: build.mutation<SkipIdVerificationResProps, SkipIdVerificationReqProps>({
      query: ({ appointmentId }) => ({
        method: 'PATCH',
        url: `/appointments/${appointmentId}/skip-id-verification`
      })
    }),
    updateAppointment: build.mutation<UpdateAppointmentResProps, UpdateAppointmentReqProps>({
      query: ({ accessToken, appointmentId, ...body }) => ({
        body,
        headers: {
          'API-KEY': import.meta.env.VITE_API_KEY,
          Authorization: accessToken
        },
        method: 'PATCH',
        url: `/appointments/${appointmentId}`
      })
    }),
    updateCallErrors: build.mutation<UpdateCallErrorsResProps, UpdateCallErrorsReqProps>({
      query: ({ appointmentId, ...body }) => ({
        body: {
          ...body,
          callType: 'video'
        },
        headers: { Platform: 'web-patient' },
        method: 'POST',
        url: `/appointments/${appointmentId}/call-event-errors`
      })
    }),
    uploadFiles: build.mutation<UploadFilesResProps, UploadFilesReqProps>({
      query: ({ appointmentId, body }) => ({
        body,
        method: 'POST',
        url: `/appointments/${appointmentId}/upload`
      })
    })
  }),
  reducerPath: 'appointmentsApi',
  tagTypes: ['Appointments', 'UpfrontCopay']
});

export const {
  useAddAppointmentMutation,
  useCallLogTracksMutation,
  useCancelAppointmentMutation,
  useCheckApptEligibilityMutation,
  useCheckPricesPredictMutation,
  useCollectUpfrontCopayMutation,
  useGetAppointmentInfoForGuestQuery,
  useGetAppointmentMethodsQuery,
  useGetAppointmentQuery,
  useGetAppointmentsQuery,
  useGetAppointmentTypeQuery,
  useGetCancellationFeePreviewQuery,
  useGetIntakeRequirementsQuery,
  useGetQueueEstimatePreviewQuery,
  useGetUpfrontCopayPreviewQuery,
  useLazyGetAppointmentMethodsQuery,
  useLazyGetAppointmentQuery,
  useLazyGetAppointmentsQuery,
  useLazyGetIntakeRequirementsQuery,
  useLazyGetNoShowPolicyQuery,
  useLazyGetTokenZoomQuery,
  useMissedBannerViewedMutation,
  usePoorConnectionMutation,
  useRescheduleAppointmentMutation,
  useSkipIdVerificationMutation,
  useUpdateAppointmentMutation,
  useUpdateCallErrorsMutation,
  useUploadFilesMutation
} = appointmentsApi;
